import { PropertiesFillDialog } from '@pages/files/dialogs/PropertiesFillDialog';
import { DocumentPropertiesService, IDocumentProperty, IDocumentPropertyAssign } from '@shared/services/DocumentPropertiesService';
import { LStorage } from '@shared/Storage';
import { Button } from '@shared/ui-react/Button';
import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import './ImageViewerWithNavigation.scss';


@observer
export class DocumentPropertiesList extends React.Component<{
    doc: IImage | IDocument,
    canEdit: boolean
}> {
    @observable isLoading = false;
    @observable properties: { [key: string]: IDocumentPropertyAssign } = {};
    @observable fillValues = {};
    @observable isCollapsed = LStorage.getOrDefault('documentPropertiesListCollapsed', false);
    fetchFolderContentAbort: AbortController = null;

    componentDidMount() {
        this.init();
    }

    componentWillUnmount() {
        if (this.fetchFolderContentAbort) {
            this.fetchFolderContentAbort.abort();
        }
    }

    componentDidUpdate(prevProps: any) {
        if (prevProps.doc?.uuid !== this.props.doc?.uuid) {
            this.properties = {};
            this.fillValues = {};
            if (this.fetchFolderContentAbort) {
                this.fetchFolderContentAbort.abort();
            }
            this.init();
        }
    }


    async init() {
        this.isLoading = true;
        this.fetchFolderContentAbort = new AbortController();

        await DocumentPropertiesService.folder.list(this.props.doc.folderUuid || this.props.doc.folder?.uuid, this.fetchFolderContentAbort.signal)
            .then(resp => {
                const properties = {};
                resp.forEach(p => {
                    const key = p.propertyDeclaration.propertyKey;
                    properties[key] = p;
                });
                this.properties = properties;
            }).catch(e => null);

        await DocumentPropertiesService.document.fetchDocument(this.props.doc.uuid, this.fetchFolderContentAbort.signal)
            .then(document => {
                if (document.properties) {
                    document.properties.forEach(p => {
                        this.fillValues[p.key] = p.value;
                    });
                }
            }).catch(e => null);

        this.isLoading = false;
    }

    constructor(props: any) {
        super(props);
        makeObservable(this);
    }

    toggleCollapsed = () => {
        this.isCollapsed = !this.isCollapsed;
        LStorage.setItem('documentPropertiesListCollapsed', this.isCollapsed);
    }

    render() {
        if (this.isLoading) {
            return <div className='flex-row' style={{ height: 28 }}>
                <span>{__('js_properties.properties')}: </span>
                <div className='loader_small'></div>
            </div>;
        }

        const EditBtn = this.props.canEdit ? <Button icon='iki iki-edit' onClick={this.openEditDialog} iconSize={16} style={{ marginLeft: 5}}
            className='ik-button-small ik-button-secondary--subtle' tooltip={__('js_files.edit_properties')} /> : null;

        const fillEntries = Object.entries(this.fillValues);

        if (fillEntries.length == 0) {
            return <div className='flex-row ai-center' style={{ height: 28 }}>{__('js_properties.no_filled_properties')} {EditBtn}</div>;
        }

        const PrintPairs = (renderer: (label: string, val: any) => React.ReactElement, skipEmpty = false) => {
            return fillEntries.filter(([key, value]) => {
                if (['model', 'make'].includes(key)) return false;
                if (!skipEmpty) return true;
                return value != null;
            })
            .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
            .map(([key, value]) => {
                const prop: IDocumentProperty = this.properties[key]?.propertyDeclaration;
                const label = !!prop?.labels && prop.labels[kuura.getLanguage()] ? prop?.labels[kuura.getLanguage()] : key;
                return renderer(label, Array.isArray(value) ? value.join(', ') : value);
            })
        }

        return <>
            <div className='flex-row ai-center gap-third'>
                <span onClick={this.toggleCollapsed} className='image-collapsible-title'>
                    <i className={!this.isCollapsed ? 'iki iki-chevron-down' : 'iki iki-chevron-up'}></i>
                    <span>{__('js_properties.properties')}</span>
                </span>
                {EditBtn}
                {this.isCollapsed && <div className='flex-row gap-third' style={{ flexWrap: 'wrap' }}>
                    {PrintPairs((label: string, val: any) => <div key={label} className='badge ghost'>{label}:&nbsp;{val}</div>, true)}
                </div>
                }
            </div>
            {!this.isCollapsed &&
                <table style={{ paddingLeft: 10 }}>
                    <tbody>
                        {PrintPairs((label: string, val: any) => <tr key={label}>
                            <td>{label}:&nbsp;&nbsp;</td>
                            <td>{val}</td>
                        </tr>, false)}
                    </tbody>
                </table>
            }
        </>;
    }

    openEditDialog = () => {
        PropertiesFillDialog.openForFolder(this.props.doc.folderUuid, [this.props.doc.uuid], (newFillValues) => {
            this.fillValues = newFillValues;
        });
    }
}
